import { useBoolean } from 'ahooks';
import {
  Affix,
  AutoComplete,
  Divider,
  Form,
  Input,
  PageHeader,
  Select,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { Headline } from 'components/RevxTypography';
import { Account } from 'domains/accountDomain';
import {
  ContactLevelEnum,
  INDUSTRY_AVG_ID,
  PLATFORM_ORG_ID,
  REVSHOP_STORE_ID,
} from 'domains/orgDomain';
import { PlatformRoleEnum, ROLE_NAMES, SiteRoleEnum } from 'domains/roleDomain';
import { useOrgId } from 'hooks/useOrg';
import { isUndefined } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import contactService from 'services/contactService';
import states from 'states-us';
import { isUnitedStates } from 'utils/address';
import {
  ADMIN_DETAIL_AFFIX_TOP,
  PLATFORM_FORM_ITEM_LAYOUT,
} from 'utils/constants';
import { RULE_EMAIL_TRIM, RULE_TEXT_LEN_255 } from 'utils/validators';
import AddressValidation, {
  ValidationRef,
} from '../ShippingAdmin/components/AddressValidation';
import { OrgTypeEnum } from '../OrgAdmin/OrgTypeSelectModal';

const statesAbbrevs = states.map((x) => x.abbreviation);

const platformAdminRoles: PlatformRoleEnum[] = [
  PlatformRoleEnum.BackendSystemAdmin,
  PlatformRoleEnum.DataCustomerServiceAdmin,
  PlatformRoleEnum.OrganizationAdmin,
  PlatformRoleEnum.NoAdminAccess,
];
const orgAdminRoles: PlatformRoleEnum[] = [
  PlatformRoleEnum.OrganizationAdmin,
  PlatformRoleEnum.NoAdminAccess,
];
const siteRoles: SiteRoleEnum[] = [
  SiteRoleEnum.Buyer,
  SiteRoleEnum.Supplier,
  SiteRoleEnum.Sourcer,
  SiteRoleEnum.NoSiteAccess,
];

const AccountItemForm = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.account.saveUser
  );
  const deleting = useAppSelector(
    (state) => state.loading.effects.account.deleteUser
  );
  const orgId = useOrgId();
  const orgData = useAppSelector((state) => state.org.list);
  const item = useAppSelector((state) => state.account.curItem);
  const currentOrg = useMemo(
    () => orgData.find((org) => org.id === orgId),
    [orgData, orgId]
  );
  const [isDataAdmin, { set: setIsDataAdmin }] = useBoolean(false);
  const [isOrgAdmin, { set: setIsOrgAdmin }] = useBoolean(false);
  const [isSourcer, { set: setIsSourcer }] = useBoolean(false);
  const [isSupplier, { set: setIsSupplier }] = useBoolean(false);
  const [canVisitStore, { set: setCanVisitStore }] = useBoolean(false);

  const [availablePlatformRoles, availableSiteRoles] = useMemo(() => {
    if (orgId === PLATFORM_ORG_ID) return [platformAdminRoles, siteRoles];
    else return [orgAdminRoles, siteRoles];
  }, [orgId]);

  // 判断是否是 SAAS 组织的 Organization Admin（自动选中所有并禁用）
  const isSaasOrgAdmin = useMemo(() => {
    return currentOrg?.orgType === OrgTypeEnum.SAAS && isOrgAdmin;
  }, [currentOrg?.orgType, isOrgAdmin]);

  // 获取过滤后的组织列表：SAAS 显示当前及下级，Customer 只显示当前
  const filteredOrgData = useMemo(() => {
    const baseFilter = orgData.filter(
      (item) =>
        item.id !== PLATFORM_ORG_ID &&
        item.id !== INDUSTRY_AVG_ID &&
        item.id !== REVSHOP_STORE_ID
    );

    if (!currentOrg) return baseFilter;

    // Customer 组织：只显示当前组织
    if (currentOrg.orgType === OrgTypeEnum.CUSTOMER) {
      return baseFilter.filter((item) => item.id === currentOrg.id);
    }

    // SAAS 组织：显示当前组织及其所有下级组织
    if (currentOrg.orgType === OrgTypeEnum.SAAS) {
      return baseFilter.filter((item) => item.parentId === currentOrg.id);
    }

    return baseFilter;
  }, [orgData, currentOrg]);

  // 获取所有可用的组织 ID
  const allAvailableOrgIds = useMemo(() => {
    return filteredOrgData.map((item) => item.id);
  }, [filteredOrgData]);

  useEffect(() => {
    setIsDataAdmin(
      item?.platformRole === PlatformRoleEnum.DataCustomerServiceAdmin
    );
    setIsOrgAdmin(item?.platformRole === PlatformRoleEnum.OrganizationAdmin);
    setIsSourcer(item?.siteRole === SiteRoleEnum.Sourcer);
    setIsSupplier(item?.siteRole === SiteRoleEnum.Supplier);
    setCanVisitStore(item?.siteRole !== SiteRoleEnum.NoSiteAccess);
  }, [
    item,
    orgId,
    setCanVisitStore,
    setIsDataAdmin,
    setIsOrgAdmin,
    setIsSourcer,
    setIsSupplier,
  ]);

  const onClose = () => {
    dispatch.account.setState({ curItem: undefined });
  };
  const onSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      // do nothing when form invalid
      return;
    }

    orgId && (values.orgId = orgId);
    values &&
      values.platformRole !== PlatformRoleEnum.OrganizationAdmin &&
      ![SiteRoleEnum.Sourcer, SiteRoleEnum.Supplier].includes(
        values.siteRole
      ) &&
      (values.manageOrgIds = []);
    values.email = values.email?.trim();

    if (values?.siteRole === SiteRoleEnum.Supplier) {
      values.allowSwitchToSupplier = values.allowSwitchToSourcer;
    } else if (values?.siteRole === SiteRoleEnum.Sourcer) {
      values.allowSwitchToSourcer = values.allowSwitchToSupplier;
      values.allowAdjustJob = true;
    } else {
      values.allowAdjustJob = false;
    }
    if (item?.id) {
      await dispatch.account.updateUser({
        ...item,
        ...values,
      });
      onClose();
    } else {
      if (canVisitStore) {
        if (values.ignoreShippingValidation) {
          await saveWithCtAddr(values);
          onClose();
        } else {
          const ret = await addrValidationRef.current?.validate({
            addressLine: values.addressLine1,
            city: values.city,
            stateProvinceCode: values.state,
            countryCode: values.country,
            postalCode: values.postalCode,
          });
          if (ret) {
            await saveWithCtAddr(values);
            onClose();
          }
        }
      } else {
        await dispatch.account.saveUser({
          ...values,
        });
        onClose();
      }
    }
  };

  const saveWithCtAddr = async (values: any) => {
    const createdAccount = await dispatch.account.saveUser({
      ...values,
    });
    await contactService.addUserDefaultContactWithAddress({
      userId: createdAccount.id,
      contact: {
        ...values,
        contactLevel: ContactLevelEnum.USER,
      },
      address: {
        ...values,
      },
    });
  };

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <Headline>
              {item?.id
                ? t('platform.account.form.title.edit')
                : t('platform.account.form.title.create')}
            </Headline>
          }
          extra={[
            <RevxButton type="primary" brandGreen onClick={onSave} key="save">
              {t('common.save')}
            </RevxButton>,
            <>
              <Divider type="vertical" />
              <RevxButton onClick={onClose} brandGreen key="cancel">
                {t('common.cancel')}
              </RevxButton>
            </>,
          ]}
        ></PageHeader>
      </Affix>
    </div>
  );

  const onValuesChange = (changedValues: any, allValue: Account) => {
    setIsDataAdmin(
      allValue.platformRole === PlatformRoleEnum.DataCustomerServiceAdmin
    );
    setIsOrgAdmin(allValue.platformRole === PlatformRoleEnum.OrganizationAdmin);
    setIsSourcer(allValue.siteRole === SiteRoleEnum.Sourcer);
    setIsSupplier(allValue.siteRole === SiteRoleEnum.Supplier);

    setCanVisitStore(allValue.siteRole !== SiteRoleEnum.NoSiteAccess);

    if (Object.keys(changedValues)[0] === 'siteRole') {
      form.setFieldsValue({
        ...allValue,
        allowSwitchToSourcer: false,
        allowSwitchToSupplier: false,
      });
    }

    // 如果是 SAAS 组织且选择了 Organization Admin，自动选中所有组织
    if (Object.keys(changedValues)[0] === 'platformRole') {
      if (
        currentOrg?.orgType === OrgTypeEnum.SAAS &&
        allValue.platformRole === PlatformRoleEnum.OrganizationAdmin
      ) {
        form.setFieldsValue({
          manageOrgIds: allAvailableOrgIds,
        });
      }
    }
  };

  const addrValidationRef = useRef<ValidationRef>(null);

  return (
    <>
      <RevxSpin spinning={saving || deleting}>
        {actionBar}
        <div className="platform-item-form-wrapper">
          <Form
            {...PLATFORM_FORM_ITEM_LAYOUT}
            layout={'horizontal'}
            form={form}
            className="platform-item-form"
            onValuesChange={onValuesChange}
            initialValues={{
              ...item,
              orgId: item?.org?.id || orgId,
              manageOrgIds:
                currentOrg?.orgType === OrgTypeEnum.SAAS &&
                item?.platformRole === PlatformRoleEnum.OrganizationAdmin
                  ? allAvailableOrgIds
                  : item?.manageOrgs?.map((item) => item.id),
            }}
          >
            <Form.Item
              label={t('platform.account.columnTitle.orgName')}
              rules={[{ required: true }]}
            >
              <Input
                disabled
                value={orgData.filter((item) => item.id === orgId)[0]?.name}
              />
            </Form.Item>
            <Form.Item noStyle hidden name="orgId" />
            <Form.Item
              label={t('platform.account.columnTitle.email')}
              name="email"
              rules={[RULE_EMAIL_TRIM('email'), { required: true }]}
            >
              <Input disabled={!!item?.id} />
            </Form.Item>
            <Form.Item
              label={t('platform.account.columnTitle.firstName')}
              name="firstName"
              rules={[RULE_TEXT_LEN_255, { required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('platform.account.columnTitle.lastName')}
              name="lastName"
              rules={[RULE_TEXT_LEN_255, { required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="SSO Account"
              name="isSSOAccount"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label={t('platform.account.form.platformRole')}
              name="platformRole"
              rules={[{ required: true }]}
            >
              <Select
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {availablePlatformRoles.map((option) => {
                  return (
                    <Select.Option value={option} key={option}>
                      {ROLE_NAMES[option]}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              label={t('platform.account.form.siteRole')}
              name="siteRole"
              rules={[{ required: true }]}
            >
              <Select
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {availableSiteRoles.map((option) => {
                  return (
                    <Select.Option value={option} key={option}>
                      {ROLE_NAMES[option]}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            {isSupplier && (
              <>
                <Form.Item
                  label={t('platform.account.form.switchToSourcer')}
                  name="allowSwitchToSourcer"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label={t('platform.account.form.adjustAllJobs')}
                  name="allowAdjustJob"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}
            {isSourcer && (
              <Form.Item
                label={t('platform.account.form.switchToSupplier')}
                name="allowSwitchToSupplier"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            )}
            {(isDataAdmin || isSourcer || isSupplier || isSaasOrgAdmin) && (
              <Form.Item
                label={t('platform.account.columnTitle.orgIdsName')}
                name="manageOrgIds"
              >
                <Select
                  showSearch
                  mode="multiple"
                  disabled={isSaasOrgAdmin}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      ?.indexOf(input?.toLowerCase()) >= 0
                  }
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                  dropdownRender={(menu) => {
                    const allOrgIds = filteredOrgData.map((item) => item.id);
                    const currentValues =
                      form.getFieldValue('manageOrgIds') || [];
                    const isAllSelected =
                      currentValues.length === allOrgIds.length;

                    return (
                      <>
                        <div
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            fontWeight: 500,
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          onClick={() => {
                            if (isAllSelected) {
                              // form.setFieldsValue({ manageOrgIds: [] });
                            } else {
                              form.setFieldsValue({ manageOrgIds: allOrgIds });
                            }
                          }}
                        >
                          {isAllSelected ? '✓ ' : ''}
                          {t('common.selectAll')}
                        </div>
                        {menu}
                      </>
                    );
                  }}
                >
                  {filteredOrgData.map((option) => {
                    return (
                      <Select.Option value={option.id} key={option.id}>
                        {option.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}
            {canVisitStore && isUndefined(item?.id) && (
              <>
                <Form.Item
                  label={t('platform.org.ct.table.columns.title')}
                  name={'title'}
                  rules={[RULE_TEXT_LEN_255]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.ct.table.columns.department')}
                  name={'department'}
                  rules={[RULE_TEXT_LEN_255]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.ct.table.columns.costCenter')}
                  name={'costCenter'}
                  rules={[RULE_TEXT_LEN_255]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.ct.table.columns.company')}
                  name={'company'}
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input />
                </Form.Item>

                <AddressValidation
                  address={{
                    addressLine: form.getFieldValue('addressLine1'),
                    city: form.getFieldValue('city'),
                    stateProvinceCode: form.getFieldValue('state'),
                    countryCode: form.getFieldValue('country'),
                    postalCode: form.getFieldValue('postalCode'),
                  }}
                  onApply={(record) => {
                    form.setFieldsValue({
                      addressLine1: record.addressLine,
                      city: record.city,
                      state: record.stateProvinceCode,
                      country: record.countryCode,
                      postalCode: record.postalCode,
                    });
                  }}
                  ref={addrValidationRef}
                />

                <Form.Item
                  label={t('platform.org.addr.table.columns.addressLine1')}
                  name={'addressLine1'}
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.addr.table.columns.addressLine2')}
                  name={'addressLine2'}
                  rules={[RULE_TEXT_LEN_255]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.addr.table.columns.city')}
                  name="city"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) => prev.country !== cur.country}
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      label={t('platform.org.addr.table.columns.state')}
                      // only US addresses need a state; most countries have none (PRD-746).
                      // `dependencies` re-validates when country changes, and the rule reads
                      // it as it validates, so the error clears instead of sticking.
                      dependencies={['country']}
                      required={isUnitedStates(getFieldValue('country'))}
                      rules={[
                        RULE_TEXT_LEN_255,
                        ({ getFieldValue: getCurrent }) => ({
                          required: isUnitedStates(getCurrent('country')),
                        }),
                      ]}
                      name="state"
                    >
                      {/* the list is US state codes, so offer it only for US addresses;
                          elsewhere a province is free text (PRD-746) */}
                      {isUnitedStates(getFieldValue('country')) ? (
                        <AutoComplete>
                          {statesAbbrevs.map((s) => (
                            <AutoComplete.Option value={s} key={s}>
                              {s}
                            </AutoComplete.Option>
                          ))}
                        </AutoComplete>
                      ) : (
                        <Input />
                      )}
                    </Form.Item>
                  )}
                </Form.Item>

                <Form.Item
                  label={t('platform.org.addr.table.columns.country')}
                  name="country"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.addr.table.columns.postalCode')}
                  name="postalCode"
                  rules={[RULE_TEXT_LEN_255, { required: true }]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t('platform.org.addr.table.columns.phoneNumber')}
                  name="phoneNumber"
                  rules={[RULE_TEXT_LEN_255]}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  label={t(
                    'platform.org.addr.form.labels.ignoreShippingValidation'
                  )}
                  name="ignoreShippingValidation"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}
            {canVisitStore && !isUndefined(item?.id) && (
              <Form.Item
                label={t('platform.org.ct.form.label.balanceBuyerId')}
                name={'balanceBuyerId'}
                rules={[RULE_TEXT_LEN_255]}
                hidden
              >
                <Input />
              </Form.Item>
            )}
          </Form>
        </div>
      </RevxSpin>
    </>
  );
};
export default AccountItemForm;
